import { useEffect, useMemo, useState } from "react";
import z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, CheckboxMultiSelect } from "@/components";
import { useShopStore } from "@/store/shopStore";
import { useGroupStore } from "@/store/groupStore";
import { toastError, toastSuccess } from "@/utils/toastify";
import { ApiResponse, DEFAULT_SUCCESS_MESSAGE, GetStoresResp } from "@/types";
import { useCompanyStore } from "@/store/companyStore";

export type EditGroupFormData = {
  name: string;
  stores?: number[];
};

const FIRST_PAGE = 1;

const schemaGroup = z.object({
  name: z.string().trim().min(1, "この項目は必須です。"),
  stores: z.array(z.number()).optional(),
});

export const EditGroup = ({
  id,
  open,
  setOpen,
  defaultValues,
}: {
  id: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  defaultValues: EditGroupFormData;
}) => {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCreateGroup, setLoadingUpdateGroup] = useState(false);
  const [hasMore, setHasMore] = useState(false); //TODO: Update loading inscroll sang load hết;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
  } = useForm<EditGroupFormData>({
    resolver: zodResolver(schemaGroup),
    defaultValues: {
      name: defaultValues.name,
      stores: defaultValues.stores,
    },
    mode: "onChange",
  });
  const { updateGroup, groupsInCompany, setGroupsInCompany } = useGroupStore();
  const { shopsInCompany, fetchShopsInCompany, setShopsInCompany } =
    useShopStore();
  const { stores, setStores, company } = useCompanyStore();

  const companyId = Number(company?.id);

  const storeDisplayName = useMemo(() => {
    return company?.store_display_name || "店舗";
  }, [company]);

  const loadOptions = async (page: number) => {
    if (isLoading || !companyId) return;
    setIsLoading(true);
    const response = await fetchShopsInCompany({
      companyId: companyId,
      page: page,
      isPagination: 0, // update loading inscroll sang load hết
    });
    const activeOptions = [
      ...(page == FIRST_PAGE ? [] : shopsInCompany),
      ...response.data.map((item) => ({ label: item.label, value: item.key })),
    ];
    setShopsInCompany(activeOptions);
    setHasMore(false); // update loading inscroll sang load hết
    setIsLoading(false);
  };

  useEffect(() => {
    if (!open) return; // Chỉ load khi modal mở
    setPage(FIRST_PAGE);
    setShopsInCompany([]);
    loadOptions(FIRST_PAGE);
  }, [open]);

  const handleScrollToBottom = () => {
    if (hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOptions(nextPage);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async (data: EditGroupFormData) => {
    const oldStores = stores;
    try {
      setLoadingUpdateGroup(true);
      const storesOfGroup = new Set(data.stores || []);
      setStores({
        ...stores,
        data: (stores?.data || [])?.map((i) => {
          if (storesOfGroup.has(i.id)) {
            return {
              ...i,
              group: {
                id: id || 0,
                name: data.name?.trim() ?? "",
              },
            };
          }
          if (i.group?.id === id) {
            return {
              ...i,
              group: null,
            };
          }
          return i;
        }),
      } as ApiResponse<GetStoresResp[]>);
      const resp = await updateGroup(id, {
        name: data.name?.trim(),
        storeIds: data.stores || [],
      });
      toastSuccess(resp?.message || DEFAULT_SUCCESS_MESSAGE);
      reset();
      const newGroupInCompany = {
        ...groupsInCompany,
        data: (groupsInCompany.data || []).map((i) =>
          i.value === id ? { label: data?.name || "", value: id } : i
        ),
      };
      setGroupsInCompany(newGroupInCompany);
      handleClose();
    } catch (error: any) {
      // back lại dữ liệu cũ khi có lỗi
      setStores(oldStores);

      if (error?.error === "name") {
        setError("name", { message: error.message });
      } else {
        toastError(error.message);
      }
    } finally {
      setLoadingUpdateGroup(false);
    }
  };

  // Tạo Map để lookup O(1) thay vì includes O(n)
  const storeMap = useMemo(() => {
    return new Map(shopsInCompany.map((store) => [store.value, store]));
  }, [shopsInCompany]);

  return (
    <Modal
      title="編集"
      open={open}
      overflowScroll={false}
      onOpenChange={loadingCreateGroup ? () => {} : setOpen}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-8">
          <Input
            label="グループ名"
            required
            {...register("name")}
            disabled={loadingCreateGroup}
            error={errors.name?.message}
            maxLength={150}
          />
          <Controller
            control={control}
            name="stores"
            render={({ field: { onChange, value } }) => {
              const selectedStores =
                value && value?.length > 0
                  ? value
                      .map((storeValue) => storeMap.get(storeValue))
                      .filter(Boolean)
                  : [];
              return (
                <CheckboxMultiSelect
                  label={`所属${storeDisplayName}`}
                  placeholder={`所属${storeDisplayName}を選択してください`}
                  isSearchable={true}
                  isClearable={true}
                  isLoading={isLoading}
                  options={shopsInCompany}
                  value={selectedStores as any}
                  isDisabled={loadingCreateGroup}
                  onChange={(data) => {
                    const values = data ? data.map((item) => item.value) : [];
                    onChange(values);
                  }}
                  onMenuScrollToBottom={handleScrollToBottom}
                />
              );
            }}
          />
        </div>
        <div className="flex justify-end gap-4 mt-4">
          <Button
            buttonType="text"
            className="!h-12"
            type="button"
            onClick={handleClose}
            disabled={loadingCreateGroup}
          >
            キャンセル
          </Button>

          <Button
            buttonType="primary"
            className="!h-12"
            type="submit"
            disabled={!!errors.name}
            isLoading={loadingCreateGroup}
          >
            更新する
          </Button>
        </div>
      </form>
    </Modal>
  );
};
